import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../../context/AppContext';
import useAppSettings from '../../../hooks/useAppSettings';
import GradientPainter from '../../common/GradientPainter/GradientPainter';
import Logo from '../../common/Logo/Logo';
import useAnalytics from '../../../hooks/useAnalytics';

import './Game2.scss';

const Game2 = () => {
  const [currentScreen, setCurrentScreen] = useState('intro');
  const [currentLevel, setCurrentLevel] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [startTime, setStartTime] = useState(0);
  const [levelTimes, setLevelTimes] = useState([]);
  const { actions } = useApp();
  const { homepageData } = useAppSettings();
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [showTryAgain, setShowTryAgain] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const blockedCellTimeoutRef = useRef(null);
  const lastValidCellRef = useRef(null);
  const { trackRailInspectionLoad, trackRailInspectionStart, trackRailInspectionLvlOneStart, trackRailInspectionLvlOneFinish, trackRailInspectionNextLevel, trackRailInspectionLvlTwoStart, trackRailInspectionLvlTwoFinish, trackRailInspectionLvlThreeStart, trackRailInspectionLvlThreeFinish, trackRailInspectionPlayAgain } = useAnalytics();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackRailInspectionLoad();
      hasTrackedRef.current = true;
    }
  }, [trackRailInspectionLoad]);

  const handleBackToHome = () => {
    actions.setView('home');
  };

  const levels = {
    1: {
      grid: [
        ['O', 'X', 'O'],
        ['X', 'O', 'O'],
        ['O', 'O', 'X']
      ],
      title:
        homepageData?.rr_level_complete_message_title || 'Level 1 Complete!'
    },
    2: {
      grid: [
        ['O', 'O', 'O'],
        ['X', 'O', 'O'],
        ['O', 'O', 'X'],
        ['O', 'O', 'X']
      ],
      title: 'Nice work!'
    },
    3: {
      grid: [
        ['X', 'O', 'O', 'O'],
        ['X', 'O', 'X', 'O'],
        ['O', 'O', 'O', 'X'],
        ['O', 'O', 'X', 'X'],
        ['X', 'X', 'O', 'O']
      ],
      title: 'Excellent!'
    }
  };

  const countAvailableNodes = (grid) => {
    let count = 0;
    for (let row of grid) {
      for (let cell of row) {
        if (cell === 'O') count++;
      }
    }
    return count;
  };

  const findNearestAvailableCell = (x, y) => {
    const gridElement = document.querySelector('.grid');
    if (!gridElement) return null;

    let nearestCell = null;
    let minDistance = Infinity;
    const SNAP_THRESHOLD = 80;

    document.querySelectorAll('.grid-cell.available').forEach((cell) => {
      const rect = cell.getBoundingClientRect();
      const cellCenterX = rect.left + rect.width / 2;
      const cellCenterY = rect.top + rect.height / 2;

      const distance = Math.sqrt(
        Math.pow(x - cellCenterX, 2) + Math.pow(y - cellCenterY, 2)
      );

      if (distance < minDistance && distance < SNAP_THRESHOLD) {
        minDistance = distance;
        nearestCell = {
          row: parseInt(cell.dataset.row),
          col: parseInt(cell.dataset.col),
          element: cell
        };
      }
    });

    return nearestCell;
  };

  const isInBlockedCellCenter = (element, x, y) => {
    if (!element || !element.classList.contains('blocked')) return false;

    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const threshold = Math.min(rect.width, rect.height) * 0.3;

    const distance = Math.sqrt(
      Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
    );

    return distance < threshold;
  };

  useEffect(() => {
    if (showTryAgain) {
      const timeout = setTimeout(() => {
        setShowTryAgain(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [showTryAgain]);

  useEffect(() => {
    return () => {
      if (blockedCellTimeoutRef.current) {
        clearTimeout(blockedCellTimeoutRef.current);
      }
    };
  }, []);

  const startGame = () => {
    trackRailInspectionStart();
    trackRailInspectionLvlOneStart();
    setCurrentScreen('game');
    initializeLevel(1);
  };

  const initializeLevel = (level) => {
    setCurrentLevel(level);
    setCurrentPath([]);
    setStartTime(Date.now());
    setShowTryAgain(false);
    setIsResetting(false);
    lastValidCellRef.current = null;
  };

  const startDrawing = (row, col, cellType) => {
    if (cellType !== 'available') return;
    setIsDrawing(true);
    setCurrentPath([{ row, col }]);
    setShowTryAgain(false);
    setIsResetting(false);
    lastValidCellRef.current = { row, col };
  };

  const addToPath = (row, col, cellType, clientX, clientY) => {
    if (!isDrawing) return;

    if (blockedCellTimeoutRef.current) {
      clearTimeout(blockedCellTimeoutRef.current);
      blockedCellTimeoutRef.current = null;
    }

    if (cellType === 'blocked') {
      const element = document.querySelector(
        `[data-row="${row}"][data-col="${col}"]`
      );

      if (isInBlockedCellCenter(element, clientX, clientY)) {
        blockedCellTimeoutRef.current = setTimeout(() => {
          resetPath();
        }, 200);
      } else if (clientX && clientY) {
        const nearestCell = findNearestAvailableCell(clientX, clientY);
        if (nearestCell && lastValidCellRef.current) {
          if (
            nearestCell.row !== lastValidCellRef.current.row ||
            nearestCell.col !== lastValidCellRef.current.col
          ) {
            addToPathDirect(nearestCell.row, nearestCell.col);
          }
        }
      }
      return;
    }

    addToPathDirect(row, col);
  };

  const addToPathDirect = (row, col) => {
    setCurrentPath((prevPath) => {
      const existingIndex = prevPath.findIndex(
        (p) => p.row === row && p.col === col
      );
      if (existingIndex !== -1) {
        return prevPath.slice(0, existingIndex + 1);
      }
      lastValidCellRef.current = { row, col };
      return [...prevPath, { row, col }];
    });
  };

  const endDrawing = () => {
    if (!isDrawing) return;

    if (blockedCellTimeoutRef.current) {
      clearTimeout(blockedCellTimeoutRef.current);
      blockedCellTimeoutRef.current = null;
    }

    setIsDrawing(false);
    checkPath();
  };

  const resetPath = () => {
    if (blockedCellTimeoutRef.current) {
      clearTimeout(blockedCellTimeoutRef.current);
      blockedCellTimeoutRef.current = null;
    }

    setIsResetting(true);
    setCurrentPath([]);
    setIsDrawing(false);
    setShowTryAgain(true);
    lastValidCellRef.current = null;

    setTimeout(() => {
      setIsResetting(false);
    }, 100);
  };

  const checkPath = () => {
    const gridData = levels[currentLevel].grid;
    const totalAvailableNodes = countAvailableNodes(gridData);

    if (currentPath.length === totalAvailableNodes) {

      if (currentLevel === 1) {
        trackRailInspectionLvlOneFinish();
      } else if (currentLevel === 2) {
        trackRailInspectionLvlTwoFinish();
      } else if (currentLevel === 3) {
        trackRailInspectionLvlThreeFinish();

      }

      levelComplete();
    } else {
      resetPath();
    }
  };

  const levelComplete = () => {
    const timeElapsed = Math.floor((Date.now() - startTime) / 1000);
    setLevelTimes((prev) => [
      ...prev,
      { level: currentLevel, time: timeElapsed }
    ]);
    setCurrentScreen('completion');
  };

  const nextLevel = () => {
    if (currentLevel < 3) {
      trackRailInspectionNextLevel(currentLevel);

      const nextLevelNum = currentLevel + 1;

      if (nextLevelNum === 2) {
        trackRailInspectionLvlTwoStart();
      }

      if (nextLevelNum === 3) {
        trackRailInspectionLvlThreeStart();
      }

      setCurrentLevel(nextLevelNum);
      setCurrentScreen('game');
      initializeLevel(nextLevelNum);
    } else {
      setCurrentScreen('all-complete');
    }
  };

  const playAgain = () => {
    trackRailInspectionPlayAgain();
    setCurrentLevel(1);
    setLevelTimes([]);
    setCurrentScreen('game');
    initializeLevel(1);
  };

  const handleTouchStart = (e, rowIndex, colIndex, cellType) => {
    startDrawing(rowIndex, colIndex, cellType);
  };

  const handleTouchMove = (e) => {
    if (isDrawing) {
      const touch = e.touches[0];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);

      if (element && element.classList.contains('grid-cell')) {
        const row = parseInt(element.dataset.row);
        const col = parseInt(element.dataset.col);
        const type = element.classList.contains('blocked')
          ? 'blocked'
          : 'available';
        addToPath(row, col, type, touch.clientX, touch.clientY);
      } else {
        const nearestCell = findNearestAvailableCell(
          touch.clientX,
          touch.clientY
        );
        if (nearestCell && lastValidCellRef.current) {
          if (
            nearestCell.row !== lastValidCellRef.current.row ||
            nearestCell.col !== lastValidCellRef.current.col
          ) {
            addToPathDirect(nearestCell.row, nearestCell.col);
          }
        }
      }
    }
  };

  const handleTouchEnd = () => {
    endDrawing();
  };

  const handleMouseMove = (e, rowIndex, colIndex, cellType) => {
    if (isDrawing) {
      addToPath(rowIndex, colIndex, cellType, e.clientX, e.clientY);
    }
  };

  const renderGrid = () => {
    const gridData = levels[currentLevel].grid;
    const cols = gridData[0].length;
    const rows = gridData.length;

    return (
      <div
        className={`grid level-${currentLevel}`}
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`
        }}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {gridData.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const isConnected = currentPath.some(
              (p) => p.row === rowIndex && p.col === colIndex
            );
            const cellType = cell === 'X' ? 'blocked' : 'available';

            return (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={`grid-cell ${cellType} ${isConnected ? 'connected' : ''} ${isResetting ? 'resetting' : ''}`}
                data-row={rowIndex}
                data-col={colIndex}
                onTouchStart={(e) =>
                  handleTouchStart(e, rowIndex, colIndex, cellType)
                }
                onMouseDown={() => startDrawing(rowIndex, colIndex, cellType)}
                onMouseEnter={(e) =>
                  handleMouseMove(e, rowIndex, colIndex, cellType)
                }
                onMouseUp={endDrawing}
              />
            );
          })
        )}
      </div>
    );
  };

  return (
    <div className="game2">
      <GradientPainter />

      <div className="gradient-background gradient-paint"></div>

      {(currentScreen === 'intro' || currentScreen === 'all-complete') && (
        <div className="slides-topbar-main game-screen">
            <div className="logo-group logo-group--main">
              <div className="logo logo-main-container">
                <Logo
                  className="logo-main"
                  src={
                    homepageData?.level_62_logo_white?.url ||
                    homepageData?.level_62_logo_white
                  }
                  alt={homepageData?.level_62_logo_white?.alt || 'Level 62 Logo'}
                />
              </div>
            </div>
          </div>
      )}

      {currentScreen === 'intro' && (
        <div className="screen game-screen">
          <div className="intro-content">
            <h1 className="game-title">Rail Inspection</h1>
            <p className="game-description">
              {homepageData?.rr_intro_screen_copy_1}
            </p>
            <p className="game-description">
              {homepageData?.rr_intro_screen_copy_2}
            </p>
            <div className="game-image">
              <div className="icon-box"></div>
            </div>
            <p className="game-instructions">
              {homepageData?.rr_intro_instructions}
            </p>
            <div className="game-navigation">
              <button className="btn-primary" onClick={handleBackToHome}>
                Back
              </button>
              <button className="btn-primary inverted" onClick={startGame}>
                Start
              </button>
            </div>
          </div>
        </div>
      )}

      {currentScreen === 'game' && (
        <div className="screen game-screen">
          <div className="game-header">
            <div className="level-indicator">Level {currentLevel}</div>
            <button
              className="exit-button"
              onClick={() => setShowExitPopup(true)}
            >
              Exit X
            </button>
          </div>

          <div className="game-instructions-header">
            <p className="main-instruction piped piped-inverted">
              {homepageData?.rr_active_game_instructions}
            </p>
            {showTryAgain && (
              <p className="try-again-message">
                {homepageData?.rr_game_fail_state_message}
              </p>
            )}
          </div>

          <div className="game-container">{renderGrid()}</div>
        </div>
      )}

      {currentScreen === 'completion' && (
        <div className="screen completion-screen">
          <div className="game-header">
            <div className="level-indicator">Level {currentLevel}</div>
            <button
              className="exit-button"
              onClick={() => setShowExitPopup(true)}
            >
              Exit X
            </button>
          </div>
          <div className="completion-content">
            <h1 className="completion-title">{levels[currentLevel].title}</h1>
            <p className="completion-time">
              {homepageData?.rr_level_complete_message_copy}{' '}
              <span className="completion-secs">
                {levelTimes[levelTimes.length - 1]?.time || 0}s
              </span>
            </p>

            <button
              className="next-button btn-primary inverted"
              onClick={nextLevel}
            >
              {currentLevel < 3 ? 'Inspect Next Rail' : 'See Final Results'}
            </button>
          </div>
        </div>
      )}

      {currentScreen === 'all-complete' && (
        <div className="screen completion-screen all-complete-screen">
          <div className="completion-content">
            <h1 className="completion-title">
              {homepageData?.rr_all_levels_complete_message_title ||
                '🎉 All Levels Complete!'}
            </h1>
            <p className="completion-time">
              {homepageData?.rr_all_levels_complete_message_copy ||
                "You've mastered all three levels!"}
            </p>

            <div className="level-times">
              {levelTimes.map(({ level, time }) => {
                const formatTime = (seconds) => {
                  if (seconds < 60) {
                    return `${seconds}s`;
                  } else {
                    const minutes = Math.floor(seconds / 60);
                    const remainingSeconds = seconds % 60;
                    return `${minutes}m ${remainingSeconds}s`;
                  }
                };

                return (
                  <div key={level}>
                    Level {level} solved in <span>{formatTime(time)}</span>
                  </div>
                );
              })}
            </div>

            <div className="game-navigation">
              <button className="btn-primary" onClick={handleBackToHome}>
                Main Menu
              </button>
              <button className="btn-primary inverted" onClick={playAgain}>
                Play Again
              </button>
            </div>
          </div>
        </div>
      )}

      {showExitPopup && (
        <div className="popup-overlay">
          <div className="popup inverted">
            <button
              className="popup-close"
              onClick={() => setShowExitPopup(false)}
            >
              <span>×</span>
            </button>
            <div className="popup-body">
              <h3>
                Are you sure you <br />
                want to exit?
              </h3>
              <p>You will lose your progress.</p>
              <button
                className="popup-button btn-primary inverted"
                onClick={handleBackToHome}
              >
                Yes, I'm sure
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Game2;
